// ============================================================================
//  Servicio de datos del Portal de seguimiento (incidencias y proyectos CX)
// ----------------------------------------------------------------------------
//  LECTURA (GET): ya CONECTADO al servicio ORDS real
//     GET {ORDS_BASE}/incidencias  ->  { ERROR_ID, TOTAL, INCIDENCIAS: [...] }
//
//  ESCRITURA: los servicios de "crear incidencia" y "cambiar estatus / avance"
//  AÚN NO EXISTEN. Mientras tanto, esas operaciones se aplican de forma
//  OPTIMISTA sobre la caché en memoria (marcadas con `// TODO ORDS (pendiente)`),
//  para que la UI siga siendo funcional. Al recargar se vuelve a leer del
//  servidor, por lo que esos cambios locales no persisten todavía.
// ============================================================================

import { apiGet, apiPost, apiPut, apiDelete } from "./api";
import { sesion, conAuth } from "./cx.service";
import {
  pad,
  idDe,
  normEstatus,
  normPrioridad,
  normTxt,
  parseFecha,
} from "../components/dashboard/portal/portal.helpers";
import { AVANCE_ESTATUS } from "../components/dashboard/portal/portal.constants";

// Ruta del servicio de lectura (relativa a VUE_APP_ORDS_BASE en services/api.js).
const INCIDENCIAS_PATH = "/incidencias";

// Caché en memoria de la última lectura; las escrituras optimistas operan aquí.
let cache = [];

// Mapa (descripción normalizada -> id) del catálogo de estatus. El PUT de
// estatus espera el id numérico (idEstatus), no la descripción. Se carga una
// sola vez desde GET /estatus y se reutiliza.
let estatusIdMap = null;

async function loadEstatusIdMap() {
  if (estatusIdMap) return estatusIdMap;
  const data = await apiGet("/estatus");
  const map = {};
  ((data && data.ESTATUS) || []).forEach((e) => {
    if (e && e.descripcion != null && e.id != null) {
      map[normTxt(e.descripcion)] = e.id;
    }
  });
  estatusIdMap = map;
  return map;
}

// Mapea un item del servicio ORDS al shape interno que consumen las vistas.
function hydrate(it) {
  const r = {
    cliente: it.cliente || "",
    solicitante: it.solicitante || "",
    solicitanteTipo: it.solicitanteTipo || "",
    canal: it.canal || "",
    categoria: it.categoria || "Requerimiento",
    asunto: it.asunto || "",
    prioridad: normPrioridad(it.prioridad),
    area: it.areaResponsable || it.area || "Otros",
    fechaInicial: parseFecha(it.fechaInicial),
    fechaCompromiso: parseFecha(it.fechaCompromiso),
    fechaFinal: parseFecha(it.fechaFinal),
    estatus: normEstatus(it.estatus),
    // El servicio hoy envía avance=0 en casi todos los registros (campo aún no
    // poblado). Tratamos 0/null como "sin dato" para derivar el avance del
    // estatus (Concluido→100, En proceso→50, Stand by→20). Un avance real
    // (>0) sí se respeta cuando el backend lo empiece a mandar.
    avance: it.avance ? it.avance : null,
    comentarios: it.comentarios || "",
  };
  const n = it.idClaim != null ? it.idClaim : null;
  // folioServer: folio real del backend (necesario para el DELETE). Puede venir
  // null en registros antiguos; folio (display) cae a uno derivado del idClaim.
  const folioServer = it.folio || null;
  return {
    ...r,
    idClaim: n,
    id: n != null ? String(n) : idDe(r),
    _n: n != null ? n : 0,
    folioServer,
    folio: folioServer || "INC-" + String(n != null ? n : "").padStart(4, "0"),
  };
}

// Normaliza un registro de importación (Excel/JSON del puente) al shape interno.
function normalizeRegistro(r) {
  return {
    cliente: r.cliente || "",
    solicitante: r.solicitante || "",
    solicitanteTipo: r.solicitanteTipo || "",
    canal: r.canal || "",
    categoria: r.categoria || "Requerimiento",
    asunto: r.asunto || "",
    prioridad: normPrioridad(r.prioridad),
    area: r.areaResponsable || r.area || "Otros",
    fechaInicial: parseFecha(r.fechaInicial),
    fechaCompromiso: parseFecha(r.fechaCompromiso),
    fechaFinal: parseFecha(r.fechaFinal),
    estatus: normEstatus(r.estatus),
    avance: r.avance != null ? r.avance : null,
    comentarios: r.comentarios || "",
  };
}

export const portalService = {
  // GET real: catálogos para el alta (ESTATUS, PRIORIDADES, CATEGORIAS).
  // Valida ERROR_ID === 200. Devuelve arreglos de descripciones para los <select>.
  async fetchCatalogos() {
    const data = await apiGet("/estatus");
    if (!data || Number(data.ERROR_ID) !== 200) {
      throw new Error("Catálogos: ERROR_ID=" + (data && data.ERROR_ID));
    }
    const desc = (arr) => (arr || []).map((x) => x.descripcion).filter(Boolean);
    return {
      estatus: desc(data.ESTATUS),
      prioridades: desc(data.PRIORIDADES),
      categorias: desc(data.CATEGORIAS),
    };
  },

  // GET real: catálogo de clientes/cuentas (colección ORDS estándar).
  // Devuelve [{ idcuenta, nombre, estatus }] ordenado por nombre.
  async fetchCuentas() {
    const s = await sesion().catch(() => ({ idColaborador: null, rol: "" }));
    const data = await apiGet(conAuth("/cuentas", s));
    const items = (data && data.items) || [];
    return items
      .slice()
      .sort((a, b) =>
        String(a.nombre || "").localeCompare(String(b.nombre || ""), "es")
      );
  },

  // GET real: lee todas las incidencias del servicio ORDS.
  async fetchRegistros() {
    const s = await sesion().catch(() => ({ idColaborador: null, rol: "" }));
    const data = await apiGet(conAuth(INCIDENCIAS_PATH, s));
    if (data && data.ERROR_ID != null && Number(data.ERROR_ID) !== 200) {
      throw new Error("Servicio incidencias respondió ERROR_ID=" + data.ERROR_ID);
    }
    const items = (data && data.INCIDENCIAS) || [];
    cache = items.map(hydrate);
    return cache.slice();
  },

  // Crea una incidencia vía POST real: POST {ORDS_BASE}/incidencias
  // Valida que la respuesta traiga ERROR_ID === 200 (cualquier otro = error).
  // Recibe el formulario del modal y arma el body con el shape que espera ORDS.
  async createRegistro(form) {
    const hoy = new Date().toISOString().slice(0, 10);
    const body = {
      cliente: form.cliente || "",
      solicitante: form.solicitante || "",
      solicitanteTipo: form.solicitanteTipo || "",
      canal: form.canal || "",
      categoria: form.categoria || "Requerimiento",
      asunto: form.asunto || "",
      prioridad: form.prioridad || "",
      areaResponsable: form.area || "",
      fechaInicial: form.fechaInicial || hoy,
      fechaCompromiso: form.fechaCompromiso || null,
      fechaFinal: null,
      estatus: form.estatus || "En proceso",
      avance: form.avance != null ? form.avance : 0,
      comentarios: form.comentarios || "",
    };
    const resp = await apiPost("/incidencias", body);
    if (!resp || Number(resp.ERROR_ID) !== 200) {
      throw new Error(
        "El servicio respondió ERROR_ID=" + (resp && resp.ERROR_ID) + " al crear."
      );
    }
    // Releemos del servidor para traer la incidencia con su idClaim/folio real.
    return this.fetchRegistros();
  },

  // Cambia el estatus de una incidencia vía PUT real:
  //   PUT {ORDS_BASE}/incidencias/{folio}/estatus  body { idEstatus }
  // La respuesta trae ERROR_ID===200 y el avance resultante. Actualiza la caché
  // con el estatus/avance devueltos. Registros sin folio del backend (p. ej.
  // importados localmente) caen a la actualización optimista local.
  async updateEstatus(id, estatus) {
    const item = cache.find((c) => c.id === id);
    if (!item || !item.folioServer) {
      return this.updateEstatusLocal(id, estatus);
    }
    const map = await loadEstatusIdMap();
    const idEstatus = map[normTxt(estatus)];
    if (idEstatus == null) {
      throw new Error("Estatus no reconocido en el catálogo: " + estatus);
    }
    const resp = await apiPut(
      "/incidencias/" + encodeURIComponent(item.folioServer) + "/estatus",
      { idEstatus }
    );
    if (!resp || Number(resp.ERROR_ID) !== 200) {
      throw new Error(
        "El servicio respondió ERROR_ID=" +
          (resp && resp.ERROR_ID) +
          " al actualizar el estatus."
      );
    }
    const nuevoEstatus = normEstatus(estatus);
    cache = cache.map((c) => {
      if (c.id !== id) return c;
      const upd = {
        ...c,
        estatus: nuevoEstatus,
        // El backend devuelve el avance resultante; 0/null => derivar del
        // estatus (misma convención que hydrate()).
        avance: resp.avance ? resp.avance : null,
      };
      if (nuevoEstatus === "Concluido" && !c.fechaFinal) {
        upd.fechaFinal = new Date().toISOString().slice(0, 10);
      }
      return upd;
    });
    return cache.slice();
  },

  // Fallback OPTIMISTA LOCAL: para registros sin folio del backend (no
  // persistible por PUT). No sobrevive a una recarga.
  updateEstatusLocal(id, estatus) {
    const norm = normEstatus(estatus);
    cache = cache.map((c) => {
      if (c.id !== id) return c;
      const upd = { ...c, estatus: norm, avance: AVANCE_ESTATUS[norm] };
      if (norm === "Concluido" && !c.fechaFinal)
        upd.fechaFinal = new Date().toISOString().slice(0, 10);
      return upd;
    });
    return cache.slice();
  },

  // Edita una incidencia vía PUT real:
  //   PUT {ORDS_BASE}/incidencias/{folio}/editar
  //   body { idEstatus, fechaCompromiso, comentarios }
  //   respuesta { ERROR_ID, MENSAJE, folio }
  // El body espera el id numérico del estatus (idEstatus), no la descripción;
  // se resuelve con el catálogo (loadEstatusIdMap), igual que updateEstatus.
  // NOTA: el servicio hoy acepta un ÚNICO comentario (string) — se envía el
  // último capturado. Cuando el backend soporte N comentarios se enviará la
  // lista completa (comentariosList). Los campos que el servicio aún no
  // persiste (avance, fechaFinal, comentariosList) se aplican OPTIMISTA sobre
  // la caché para que la UI los refleje; se descartan al recargar.
  // Registros sin folio del backend (importados localmente) caen al fallback.
  async updateRegistro(id, patch) {
    const item = cache.find((c) => c.id === id);
    if (!item || !item.folioServer) {
      const list = this.updateRegistroLocal(id, patch);
      return {
        list,
        mensaje: "Cambios guardados localmente (incidencia sin folio del backend).",
      };
    }
    const map = await loadEstatusIdMap();
    const idEstatus = map[normTxt(patch.estatus)];
    if (idEstatus == null) {
      throw new Error("Estatus no reconocido en el catálogo: " + patch.estatus);
    }
    const body = {
      idEstatus,
      fechaCompromiso: patch.fechaCompromiso || null,
      // El servicio aún acepta un solo comentario (string); enviamos el último.
      // TODO ORDS: enviar patch.comentariosList cuando el backend soporte N.
      comentarios: patch.comentarios || "",
    };
    const resp = await apiPut(
      "/incidencias/" + encodeURIComponent(item.folioServer) + "/editar",
      body
    );
    if (!resp || Number(resp.ERROR_ID) !== 200) {
      throw new Error(
        "El servicio respondió ERROR_ID=" +
          (resp && resp.ERROR_ID) +
          " al editar la incidencia."
      );
    }
    // Aplica el patch completo sobre la caché (incluye campos no persistidos aún).
    cache = cache.map((c) => (c.id === id ? { ...c, ...patch } : c));
    return {
      list: cache.slice(),
      mensaje: resp.MENSAJE || "Incidencia actualizada correctamente.",
    };
  },

  // Fallback OPTIMISTA LOCAL: para registros sin folio del backend (no
  // persistible por PUT). No sobrevive a una recarga.
  updateRegistroLocal(id, patch) {
    cache = cache.map((c) => (c.id === id ? { ...c, ...patch } : c));
    return cache.slice();
  },

  // Elimina una incidencia vía DELETE real: DELETE {ORDS_BASE}/incidencias/{folio}
  // Valida ERROR_ID === 200. Devuelve { list, mensaje } para notificar al usuario.
  async deleteRegistro(folio) {
    const resp = await apiDelete("/incidencias/" + encodeURIComponent(folio));
    if (!resp || Number(resp.ERROR_ID) !== 200) {
      throw new Error(
        "El servicio respondió ERROR_ID=" + (resp && resp.ERROR_ID) + " al eliminar."
      );
    }
    const list = await this.fetchRegistros();
    return { list, mensaje: resp.MENSAJE || "Incidencia eliminada correctamente." };
  },

  // Importa/mezcla registros del puente CX (Excel/JSON) sobre la caché local.
  async importRegistros(nuevos) {
    const byId = new Map(cache.map((c) => [c.id, c]));
    let maxN = cache.reduce((m, c) => Math.max(m, c._n || 0), 0);
    nuevos.map(normalizeRegistro).forEach((r) => {
      const id = idDe(r);
      const ex = byId.get(id);
      if (ex) {
        byId.set(id, { ...ex, ...r, id, folio: ex.folio, _n: ex._n });
      } else {
        maxN++;
        byId.set(id, { ...r, idClaim: null, id, _n: maxN, folio: "INC-" + pad(maxN) });
      }
    });
    cache = Array.from(byId.values());
    return { list: cache.slice(), procesados: nuevos.length };
  },

  // "Reiniciar" = volver a leer del servidor (descarta cambios locales).
  async resetRegistros() {
    return this.fetchRegistros();
  },
};

export default portalService;
